import React, { useState, useEffect } from 'react';
import './AccountConnectionModal.css';
import snaptradeLogo from '../assets/snaptrade_logo.avif';
import plaidLogo from '../assets/Plaid_logo.png';
import axios from 'axios';
import { toast } from 'react-toastify';

const AccountConnectionModal = ({ isOpen, onClose, onSnapTradeSelect, onPlaidSelect, username, onManualUploadSelect }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    brokerage: '',
    totalAssets: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const backendUrl = "https://profitree-backend-9482feeffc4a.herokuapp.com";

  // Prepopulate email from username if it looks like an email
  useEffect(() => {
    if (username && username.includes('@')) {
      setFormData(prev => ({
        ...prev,
        email: username
      }));
    }
  }, [username]);

  if (!isOpen) return null;

  const formatNumberWithCommas = (value) => {
    // Remove all non-digit characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Split by decimal point if present
    const parts = numericValue.split('.');
    // Format the integer part with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Rejoin if there was a decimal part
    return parts.join('.');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format totalAssets with commas
    if (name === 'totalAssets') {
      const formatted = formatNumberWithCommas(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    try {
      // Send form data using the new brokerage request endpoint
      const response = await axios.post(`${backendUrl}/contact/brokerage-request`, {
        email: formData.email,
        brokerageName: formData.brokerage,
        totalAssets: formData.totalAssets
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        toast.success('Thank you! Your brokerage request has been received.');
        setFormSubmitted(true);
        setTimeout(() => {
          setFormSubmitted(false);
          setShowContactForm(false);
          setFormData({ email: '', brokerage: '', totalAssets: '' });
        }, 2000);
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      if (error.response?.status === 400) {
        toast.error('Please fill in all required fields.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later or contact support directly at support@profitree-tax.com');
      } else {
        toast.error('Failed to submit request. Please try again or contact support directly at support@profitree-tax.com');
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <div className="account-connection-modal-overlay" onClick={onClose}>
      <div className="account-connection-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="account-connection-modal-header">
          <h2>Connect Your Account</h2>
          <button className="account-connection-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="account-connection-modal-body">
          <p className="account-connection-description">
            Choose how you'd like to connect your account
          </p>
          
          <div className="account-connection-options">
                <div
                  className="account-connection-option account-connection-option-recommended"
                  onClick={() => {
                    onClose(); // Close AccountConnectionModal first
                    onSnapTradeSelect(); // Then open SnapTrade modal
                  }}
                >
                  <div className="account-connection-option-header">
                    <div className="account-connection-option-icon" style={{ backgroundColor: '#ffffff', padding: '8px' }}>
                      <img 
                        src={snaptradeLogo}
                        alt="SnapTrade" 
                        onError={(e) => {
                          console.error('Failed to load SnapTrade logo:', snaptradeLogo);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('SnapTrade logo loaded successfully')}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </div>
                    <div className="account-connection-option-title">
                      <h3>SnapTrade <span className="recommended-text">(Recommended)</span></h3>
                    </div>
                  </div>
                  <p className="account-connection-option-description">
                    Securely connect your brokerage accounts with SnapTrade. Best for investment accounts and tax-loss harvesting.
                  </p>
                  <div className="account-connection-option-features">
                    <span>✓ Supports major brokerages</span>
                    <span>✓ Read-only access</span>
                    <span>✓ Bank-level security</span>
                  </div>
                </div>

                <div
                  className="account-connection-option"
                  onClick={() => {
                    onClose(); // Close AccountConnectionModal first
                    onPlaidSelect(); // Then open Plaid modal
                  }}
                >
                  <div className="account-connection-option-header">
                    <div className="account-connection-option-icon account-connection-option-icon-plaid" style={{ backgroundColor: '#ffffff', padding: '4px' }}>
                      <img 
                        src={plaidLogo}
                        alt="Plaid" 
                        onError={(e) => {
                          console.error('Failed to load Plaid logo:', plaidLogo);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Plaid logo loaded successfully')}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scale(1.3)' }}
                      />
                    </div>
                    <div className="account-connection-option-title">
                      <h3>Plaid</h3>
                      <span className="alpha-badge-inline">Alpha</span>
                    </div>
                  </div>
                  <div className="plaid-alpha-notice-inline">
                    <span className="alpha-text-inline">Plaid integration is currently in Alpha phase</span>
                  </div>
                  <p className="account-connection-option-description">
                    Connect your brokerage accounts with Plaid. Ideal for taxable investment accounts, IRAs, and 401(k)s.
                  </p>
                  <div className="account-connection-option-features">
                    <span>✓ Supports major brokerages</span>
                    <span>✓ Read-only access</span>
                    <span>✓ Secure connection</span>
                  </div>
                </div>
              </div>

              {onManualUploadSelect && (
                <div className="account-connection-manual-upload">
                  <p className="account-connection-manual-text">
                    Prefer to upload statements manually instead?
                  </p>
                  <button
                    className="btn btn-manual-upload"
                    onClick={onManualUploadSelect}
                  >
                    Upload statements manually
                  </button>
                </div>
              )}

            {/* Contact Form Section */}
            <div className="account-connection-contact-section">
                <p className="contact-section-title">
                  Did not find your brokerage?
                </p>
                <p className="contact-section-subtitle">
                  Feel free to reach out to us
                </p>
                
                {!showContactForm ? (
                  <button 
                    className="btn btn-contact"
                    onClick={() => setShowContactForm(true)}
                  >
                    Request Brokerage Support
                  </button>
                ) : (
                  <form className="contact-form" onSubmit={handleFormSubmit}>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@example.com"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="brokerage">Brokerage Looking For? *</label>
                      <input
                        type="text"
                        id="brokerage"
                        name="brokerage"
                        value={formData.brokerage}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Interactive Brokers, TD Ameritrade"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="totalAssets">Total Amount of Assets in That Brokerage? *</label>
                      <input
                        type="text"
                        id="totalAssets"
                        name="totalAssets"
                        value={formData.totalAssets}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., $50,000"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowContactForm(false);
                          setFormData({ email: '', brokerage: '', totalAssets: '' });
                        }}
                        disabled={formSubmitting}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={formSubmitting || formSubmitted}
                      >
                        {formSubmitting ? 'Submitting...' : formSubmitted ? 'Submitted!' : 'Submit Request'}
                      </button>
                    </div>
                  </form>
                )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default AccountConnectionModal;

